// =============================================================================
// Per-site storage units + probes for the full compliance module (20 Sep 2026).
//
// The kitchen app hardcoded its OWN five fridges (F1–F5) and three probes
// (P1–P3). Once the same screens run inside the Trafford site app, a Trafford
// reading logged against "F1 Walk-in fridge" would be a record of a unit that
// is not in the building. So:
//   * the lists come from the admin-managed "fridges" / "probes" config surfaces
//     (Command Centre → Compliance → Emergency fridge procedure / Probe
//     Calibration), which already carry a stable key per item;
//   * the closed Manchester kitchen keys keep the legacy lists so its history
//     still renders against the units it really had;
//   * any other site with nothing configured gets an EMPTY list — the screens
//     show a pointer to the Command Centre instead of guessing.
// Pure functions, no DOM, so they are unit-tested (complianceUnits.test.mjs).
// =============================================================================

// Site keys that mean the closed Manchester kitchen (the Apps Script backend
// folds both onto "kitchen"; the app's config key is "mcr").
export const LEGACY_KITCHEN_SITE_KEYS = ["mcr", "kitchen"];

// The closed kitchen's units, verbatim from the kitchen app. History only.
export const LEGACY_PROBES = [
  {id: "P1", name: "Probe 1 · main"},
  {id: "P2", name: "Probe 2 · backup"},
  {id: "P3", name: "Probe 3 · delivery checks"}
];
export const LEGACY_FRIDGES = [
  {id: "F1", name: "Walk-in fridge",  target: "≤+5°C"},
  {id: "F2", name: "Prep fridge",     target: "≤+5°C"},
  {id: "F3", name: "Display fridge",  target: "≤+5°C"},
  {id: "F4", name: "Fish freezer",    target: "≤−20°C"},
  {id: "F5", name: "Freezer 2",       target: "≤−18°C"}
];

export function isLegacyKitchenSite(siteKey) {
  return LEGACY_KITCHEN_SITE_KEYS.indexOf(String(siteKey || "").toLowerCase()) !== -1;
}

// The Command Centre stores a unit's safe limit as a number of °C (5 for a
// fridge, -18 for a freezer). The HACCP table shows it the way the kitchen
// always did: "≤+5°C" / "≤−18°C". A non-numeric string is shown as typed; an
// empty value falls back to the fridge limit.
export function fridgeTargetLabel(target) {
  if (target === null || target === undefined || String(target).trim() === "") return "≤+5°C";
  const n = Number(target);
  if (isNaN(n)) return String(target).trim();
  const abs = Math.abs(n);
  const num = Number.isInteger(abs) ? String(abs) : String(abs);
  return n < 0 ? "≤−" + num + "°C" : "≤+" + num + "°C";
}

function cleanList(items, mapItem) {
  const seen = {};
  const out = [];
  (Array.isArray(items) ? items : []).forEach(it => {
    if (!it || typeof it !== "object") return;
    const row = mapItem(it);
    if (!row.id || seen[row.id]) return;
    seen[row.id] = true;
    out.push(row);
  });
  return out;
}

// `items` = the ACTIVE items already resolved for this site from the "probes"
// surface ({id, label, probeKey, status}). Returns [{id, name}].
export function resolveProbes(items, siteKey) {
  const list = cleanList(items, it => {
    const key = String(it.probeKey || "").trim();
    const id = key || String(it.id || "").trim();
    return {id, name: String(it.label || "").trim() || id};
  });
  if (list.length) return list;
  return isLegacyKitchenSite(siteKey) ? LEGACY_PROBES.slice() : [];
}

// `items` = the ACTIVE items already resolved for this site from the "fridges"
// surface ({id, label, fridgeKey, target, status}). Returns [{id, name, target}].
export function resolveFridges(items, siteKey) {
  const list = cleanList(items, it => {
    const key = String(it.fridgeKey || "").trim();
    const id = key || String(it.id || "").trim();
    return {id, name: String(it.label || "").trim() || id, target: fridgeTargetLabel(it.target)};
  });
  if (list.length) return list;
  return isLegacyKitchenSite(siteKey) ? LEGACY_FRIDGES.slice() : [];
}

// The HACCP day progress denominator: every opening tick, every closing tick,
// and an AM + PM reading per configured unit (the kitchen's 8 + 6 + 10).
export function haccpTotalFor(openingCount, closingCount, unitCount) {
  return (openingCount || 0) + (closingCount || 0) + (unitCount || 0) * 2;
}
